/**
 * PulseCardStrip — the Pulse Home bottom-strip horizontal scroller (Phase 2).
 *
 * Mirrors the OEM `cardStrip.tsx` structure (fixed-size cards floating over the
 * map in a horizontal scroller), but holds the four Pulse cards in the order
 * the gripp-os PulseHomeMapView reference uses: Activity · Assets · Routines · People.
 * (OEM-only cards — Products / Customers / Campaigns — never appear on the Pulse
 * dashboard.)
 *
 * Three of the four cards are map-plottable: selecting one lifts its `HomeEntityType`
 * to the page via `onSelectPlot`, switching the map's plotting mode. Selecting
 * the active card again clears it (back to the resting ASSET view). Clicking a
 * chart item (stat pill / routine row) lifts the entity + sub-filter via
 * `onSelectStatus`.
 *
 * Web-only — do NOT import from mobile code.
 */
import React from 'react';
import { HomeEntityType } from '@gripp/shared-logic';
import { ScrollView, StyleSheet, View } from 'react-native';
import { tokens } from '@gripp/shared-ui';
import { TimeRange } from '../types';
import { PulseActivityCard } from './pulseActivityCard';
import { PulseAssetsCard } from './pulseAssetsCard';
import { PulsePeopleCard } from './pulsePeopleCard';
import { PulseRoutinesCard } from './pulseRoutinesCard';

export interface PulseCardStripProps {
  range: TimeRange;
  /** Active map plotting mode (null = resting ASSET view). */
  plotMode: HomeEntityType | null;
  /** Active sub-filter (chart item) within the plotting mode, or null. */
  statusFilter?: string | null;
  /** Toggles a plotting mode (selecting the active one again clears it). */
  onSelectPlot: (entityType: HomeEntityType) => void;
  /** Click a chart item: plot that entity and filter the map to `status`. */
  onSelectStatus?: (entityType: HomeEntityType, status: string) => void;
  /** Account id of the person the field map is filtered to (People card), or null. */
  selectedPersonId?: string | null;
  /** Toggle the person facet — scopes the field map to that person's assets. */
  onSelectPerson?: (person: { id: string; name: string }) => void;
}

/** Fixed-footprint wrapper for one card (design: w-[360px] h-[270px] snap-start). */
const StripCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <View style={styles.stripCard}>{children}</View>
);

export const PulseCardStrip: React.FC<PulseCardStripProps> = ({
  range,
  plotMode,
  statusFilter,
  onSelectPlot,
  onSelectStatus,
  selectedPersonId,
  onSelectPerson,
}) => {
  // The sub-filter only applies to whichever entity is currently plotted.
  const statusFor = (entity: HomeEntityType): string | null =>
    plotMode === entity ? statusFilter ?? null : null;
  return (
    <>
      {/* Web-only: thin, tokenized horizontal scrollbar so mouse users can reach
          the off-screen cards (gripp-os hides it and relies on trackpad scroll,
          which strands mouse users on a desktop surface). Thumb is the
          soft-text role token, composed to 50%/80% alpha with color-mix because
          a CSS variable cannot carry a hex-alpha suffix. */}
      <style>{`
        #pulse-card-scroller {
          scrollbar-width: thin;
          scrollbar-color: color-mix(in srgb, var(--text-soft) 50%, transparent) transparent;
        }
        #pulse-card-scroller::-webkit-scrollbar { height: 8px; }
        #pulse-card-scroller::-webkit-scrollbar-track { background: transparent; }
        #pulse-card-scroller::-webkit-scrollbar-thumb {
          background: color-mix(in srgb, var(--text-soft) 50%, transparent);
          border-radius: 4px;
        }
        #pulse-card-scroller::-webkit-scrollbar-thumb:hover {
          background: color-mix(in srgb, var(--text-soft) 80%, transparent);
        }
      `}</style>
      <ScrollView
        horizontal
        nativeID="pulse-card-scroller"
        showsHorizontalScrollIndicator
        contentContainerStyle={styles.content}
        style={styles.scroller}
        testID="pulse-dashboard-cardstrip"
      >
        <StripCard>
          <PulseActivityCard
            range={range}
            selected={plotMode === HomeEntityType.Activity}
            onToggleSelect={() => onSelectPlot(HomeEntityType.Activity)}
            activeStatus={statusFor(HomeEntityType.Activity)}
            onSelectStatus={(status) =>
              onSelectStatus?.(HomeEntityType.Activity, status)
            }
          />
        </StripCard>
        <StripCard>
          <PulseAssetsCard
            range={range}
            selected={plotMode === HomeEntityType.Asset}
            onToggleSelect={() => onSelectPlot(HomeEntityType.Asset)}
            activeStatus={statusFor(HomeEntityType.Asset)}
            onSelectStatus={(status) =>
              onSelectStatus?.(HomeEntityType.Asset, status)
            }
          />
        </StripCard>
        <StripCard>
          <PulseRoutinesCard
            range={range}
            selected={plotMode === HomeEntityType.Routine}
            onToggleSelect={() => onSelectPlot(HomeEntityType.Routine)}
            activeStatus={statusFor(HomeEntityType.Routine)}
            onSelectStatus={(status) =>
              onSelectStatus?.(HomeEntityType.Routine, status)
            }
          />
        </StripCard>
        {/* People is NON-PLOTTING — no plotMode / onToggleSelect wiring. It
            sets a separate `person` facet that scopes the resting field map. */}
        <StripCard>
          <PulsePeopleCard
            range={range}
            selectedPersonId={selectedPersonId}
            onSelectPerson={onSelectPerson}
          />
        </StripCard>
      </ScrollView>
    </>
  );
};

PulseCardStrip.displayName = 'PulseCardStrip';

const styles = StyleSheet.create({
  scroller: {
    // The scroller is overlaid on the map; let it size to its content height.
    flexGrow: 0,
  },
  content: {
    flexDirection: 'row',
    gap: tokens.spacing.md, // gap-3 (12)
    paddingHorizontal: tokens.spacing.lg,
    paddingBottom: tokens.spacing.lg,
    paddingTop: tokens.spacing.sm,
  },
  stripCard: {
    width: 360,
    height: 270,
  },
});
